import { createEvent, createStore, sample } from 'effector';
import type { SceneMarker } from '../shared/sceneContext';
import type { AssistantHudMessage } from './assistantHud';
import { resetNavigation } from './sceneNavigation';
import { sceneLoadingReset } from './sceneLoading';

/**
 * Per-scene state snapshot stored when the user leaves a main scene for
 * a nested inside-scene (e.g. `kaaba → kaaba-inside`). On `goBack` the
 * snapshot is restored so the user sees their previous chat history +
 * camera framing without a fresh `scene_open` round-trip or preloader
 * cinematic.
 *
 * Lifetime: session-scoped (in-memory). NOT persisted to localStorage —
 * a reload would land on `/kaaba/kaaba-inside` and a `goBack` would then
 * play the full preloader for `kaaba` (acceptable, simpler than
 * persisting + invalidating on backend / UI updates).
 *
 * Reset on `resetNavigation` (= return to map): semantically a new
 * session. Also explicitly cleared via `sceneStateSnapshotsCleared` for
 * dev/test paths.
 */
export type CameraPoseSnapshot = {
  position: { x: number; y: number; z: number };
  target: { x: number; y: number; z: number };
  fov: number;
};

export type SceneStateSnapshot = {
  messages: AssistantHudMessage[];
  cameraPose: CameraPoseSnapshot;
};

// --- Events ---

/**
 * Saved directly from non-React callers (e.g. effector samples). Keyed
 * by `poiFilterId` (= `setSceneId` payload + `splatConfig.id`), NOT by
 * URL id. This keeps the lookup in `sceneOpenRequested` filter +
 * `SceneCanvas` consistent with `$appState.sceneId` / `config.id`.
 */
export const sceneSnapshotSaved = createEvent<{
  sceneId: string;
  snapshot: SceneStateSnapshot;
}>();

/**
 * Convenience event for callers that have `sceneId` + `messages` but not
 * the camera pose — pose is read from `$sdkCameraPoseGetter` in the sample
 * below. Used by `AssistantHud.handleEnterScene`.
 *
 * With no getter published (e.g. mid-disposal), the save is silently dropped — caller can't do anything useful with a
 * partial snapshot anyway, and the next visit will go through the regular
 * scene_open flow.
 */
export const saveCurrentSceneSnapshot = createEvent<{
  sceneId: string;
  messages: AssistantHudMessage[];
}>();

/**
 * Fired by the sample below (clock: assistantChatReset) when a snapshot
 * exists for the new sceneId. `$assistantMessages` subscribes to this
 * via `.on(sceneSnapshotConsumed, (_, snap) => snap.messages)` —
 * recovering the chat for the returning scene immediately after
 * `assistantChatReset` cleared it.
 *
 * NOTE: the snapshot is intentionally NOT removed from `$sceneStateSnapshots`
 * on consume — keeping it means the `sceneOpenRequested` filter in
 * `sceneOpen.ts` reliably sees `snapshots.has(sceneId)` and skips the
 * scene_open POST (which would otherwise duplicate the intro card).
 * Snapshots are cleared on `resetNavigation` (return to map) — the only
 * exit path where a fresh session is desired. A subsequent
 * `Explore inside` re-saves the snapshot, overwriting the stale entry.
 */
export const sceneSnapshotConsumed = createEvent<SceneStateSnapshot>();

/** Manual reset escape hatch (dev / tests). */
export const sceneStateSnapshotsCleared = createEvent();

/**
 * The scene's camera-pose producer for the snapshot save (the chat + pose
 * restore on return from an inside-scene). `createSdkScene` publishes it in
 * `loadScene().then()`, nulls it on dispose; reset on every navigation
 * (`sceneLoadingReset`).
 */
export const sdkCameraPoseGetterReady = createEvent<(() => CameraPoseSnapshot) | null>();

export const $sdkCameraPoseGetter = createStore<(() => CameraPoseSnapshot) | null>(null)
  .on(sdkCameraPoseGetterReady, (_, getter) => getter)
  .reset(sceneLoadingReset);

/**
 * Producer of the chat context's `markers` note. Same lifecycle as the pose
 * getter; the closure holds the engine, so it must not outlive the scene.
 */
export const sdkSceneMarkersGetterReady = createEvent<(() => SceneMarker[]) | null>();

export const $sdkSceneMarkersGetter = createStore<(() => SceneMarker[]) | null>(null)
  .on(sdkSceneMarkersGetterReady, (_, getter) => getter)
  .reset(sceneLoadingReset);

// --- Store ---

export const $sceneStateSnapshots = createStore<Map<string, SceneStateSnapshot>>(
  new Map(),
)
  .on(sceneSnapshotSaved, (map, { sceneId, snapshot }) => {
    const next = new Map(map);
    next.set(sceneId, snapshot);
    return next;
  })
  .reset(resetNavigation, sceneStateSnapshotsCleared);

// --- Pose snapshot via the scene's pose getter ---
//
// `saveCurrentSceneSnapshot` is a high-level convenience event that
// fans out into the lower-level `sceneSnapshotSaved` after grabbing
// the camera pose from `$sdkCameraPoseGetter`. The filter drops the save
// when no scene publishes one (mid-dispose) — there's nothing useful to
// record then, and the next visit will refetch via scene_open.

sample({
  clock: saveCurrentSceneSnapshot,
  source: $sdkCameraPoseGetter,
  filter: (getter) => getter != null,
  fn: (getter, { sceneId, messages }) => {
    const pose = getter!();
    return {
      sceneId,
      snapshot: { messages, cameraPose: pose },
    };
  },
  target: sceneSnapshotSaved,
});

// --- Auto-restore on return ---
//
// Subscribes to `assistantChatReset` (which fires on every navigation
// via the sample chain in `sceneOpen.ts`). By the time this sample
// runs, `$assistantMessages.reset(assistantChatReset)` has already
// emptied the chat — so the downstream
// `$assistantMessages.on(sceneSnapshotConsumed, ...)` writes the
// restored messages onto a clean slate.
//
// Why hook on `assistantChatReset` and not on `setSceneId` directly:
// `setSceneId` has multiple subscribers (assistantChatReset,
// sceneOpenRequested, $appState.on, ...) and Effector guarantees order
// only by registration. Using `assistantChatReset` as clock decouples
// us from that order — Effector fires reducers on the event BEFORE
// chained samples, so `$assistantMessages` is guaranteed empty here.
//
// `$appState` is the source for the new sceneId — by this point the
// `$appState.on(setSceneId)` reducer has already run.
import { $appState } from './appState';
import { assistantChatReset } from './assistantEvents';

sample({
  clock: assistantChatReset,
  source: { snapshots: $sceneStateSnapshots, appState: $appState },
  filter: ({ snapshots, appState }) => {
    if (appState.sceneId == null) return false;
    return snapshots.has(String(appState.sceneId));
  },
  fn: ({ snapshots, appState }) => snapshots.get(String(appState.sceneId))!,
  target: sceneSnapshotConsumed,
});
